import type { FrameRender } from "../../../shared/shop-catalog";
import { strokeRoundedRect } from "../../utils/draw";
import { customPalette, drawCustomGrid } from "./CustomArt";

export interface FrameRect {
  x: number;
  y: number;
  w: number;
  h: number;
  radius: number;
}

export function drawFrame(
  ctx: CanvasRenderingContext2D,
  cfg: FrameRender,
  rect: FrameRect,
  time: number,
): void {
  switch (cfg.kind) {
    case "solid":
      strokeBasic(ctx, cfg.color, cfg.width, rect);
      return;
    case "bronze":
    case "silver":
    case "gold":
    case "platinum":
      drawMetallic(ctx, cfg, rect);
      return;
    case "diamond":
      drawDiamondFrame(ctx, cfg, rect);
      return;
    case "ruby":
    case "emerald":
    case "sapphire":
      drawGemFrame(ctx, cfg, rect);
      return;
    case "neon":
      drawNeonFrame(ctx, cfg, rect, time);
      return;
    case "cyber":
      drawCyberFrame(ctx, cfg, rect);
      return;
    case "steampunk":
      drawSteampunkFrame(ctx, cfg, rect, time);
      return;
    case "gothic":
      drawGothicFrame(ctx, cfg, rect);
      return;
    case "frozen":
      drawFrozenFrame(ctx, cfg, rect);
      return;
    case "flame":
      drawFlameFrame(ctx, cfg, rect, time);
      return;
    case "cosmic":
      drawCosmicFrame(ctx, cfg, rect, time);
      return;
    case "shadow":
      drawShadowFrame(ctx, cfg, rect);
      return;
    case "golden_leaf":
      drawLeafFrame(ctx, cfg, rect);
      return;
    case "crystal":
      drawCrystalFrame(ctx, cfg, rect);
      return;
    case "obsidian":
      drawObsidianFrame(ctx, cfg, rect);
      return;
    case "rainbow":
      drawRainbowFrame(ctx, cfg, rect, time);
      return;
    case "pixel":
      drawPixelFrame(ctx, cfg, rect);
      return;
    case "vintage":
      drawVintageFrame(ctx, cfg, rect);
      return;
    case "royal":
      drawRoyalFrame(ctx, cfg, rect);
      return;
    case "thorn":
      drawThornFrame(ctx, cfg, rect);
      return;
    case "lava":
      drawLavaFrame(ctx, cfg, rect, time);
      return;
    case "bubble":
      drawBubbleFrame(ctx, cfg, rect, time);
      return;
    case "sakura":
      drawSakuraFrame(ctx, cfg, rect);
      return;
    case "holographic":
      drawHolographicFrame(ctx, cfg, rect, time);
      return;
    case "ancient":
      drawAncientFrame(ctx, cfg, rect);
      return;
    case "gates_of_heaven":
      drawGatesOfHeavenFrame(ctx, cfg, rect, time);
      return;
    case "creator":
      drawCreatorFrame(ctx, cfg, rect, time);
      return;
    case "custom":
      drawCustomFrame(ctx, cfg, rect, time);
      return;
  }
}

// The animated frame border flows through the colors the player painted into
// the frame grid (so they control the animation's colors by what they draw).
// Falls back to the dominant color when only one color is present.
function customAnimStroke(
  ctx: CanvasRenderingContext2D,
  cfg: FrameRender,
  rect: FrameRect,
  time: number,
): string | CanvasGradient {
  // The player's chosen animation colors win; otherwise fall back to the colors
  // in the corner drawing.
  const chosen = (cfg.animColors ?? []).filter((c) => c);
  const pal = chosen.length ? chosen : cfg.cells && cfg.cells.length ? customPalette(cfg.cells) : [];
  if (pal.length <= 1) return pal[0] ?? cfg.color;
  const n = pal.length;
  const grad = ctx.createLinearGradient(rect.x, rect.y, rect.x + rect.w, rect.y);
  const stops = Math.max(6, n * 3);
  const phase = time * 0.5; // palette cycles per second
  for (let s = 0; s <= stops; s += 1) {
    const t = s / stops;
    const idx = (((Math.floor((t + phase) * n) % n) + n) % n);
    grad.addColorStop(t, pal[idx]!);
  }
  return grad;
}

// A custom frame: a border in the drawing's colors (flowing when animated), with
// the player's pixel grid stamped into each corner.
function drawCustomFrame(
  ctx: CanvasRenderingContext2D,
  cfg: FrameRender,
  rect: FrameRect,
  time: number,
): void {
  ctx.strokeStyle = cfg.animated ? customAnimStroke(ctx, cfg, rect, time) : cfg.color;
  ctx.lineWidth = cfg.width;
  strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
  ctx.lineWidth = 1;
  if (!cfg.cells || !cfg.grid) return;
  const s = Math.min(14, rect.w / 4, rect.h / 4);
  const corners = [
    [rect.x - s / 2, rect.y - s / 2],
    [rect.x + rect.w - s / 2, rect.y - s / 2],
    [rect.x - s / 2, rect.y + rect.h - s / 2],
    [rect.x + rect.w - s / 2, rect.y + rect.h - s / 2],
  ];
  for (const [cx, cy] of corners) drawCustomGrid(ctx, cfg.cells, cfg.grid, cx!, cy!, s, s);
}

// Rainbow border with the word "CREATOR" tiled all the way around it.
function drawCreatorFrame(
  ctx: CanvasRenderingContext2D,
  cfg: FrameRender,
  rect: FrameRect,
  time: number,
): void {
  ctx.save();
  // rainbow gradient border
  const grad = ctx.createLinearGradient(rect.x, rect.y, rect.x + rect.w, rect.y + rect.h);
  for (let i = 0; i <= 6; i += 1) {
    const hue = (i * 60 + time * 60) % 360;
    grad.addColorStop(i / 6, `hsl(${hue}, 90%, 55%)`);
  }
  ctx.strokeStyle = grad;
  ctx.lineWidth = cfg.width;
  strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);

  // "CREATOR" tiled around the border, scrolling and shimmering
  const word = "CREATOR";
  ctx.font = "bold 7px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const step = ctx.measureText(word).width + 7;
  const shift = (time * 18) % step;

  // top + bottom edges
  for (let x = rect.x + step / 2 - shift; x < rect.x + rect.w + step; x += step) {
    ctx.fillStyle = `hsl(${(((x * 2.2 + time * 80) % 360) + 360) % 360}, 90%, 60%)`;
    ctx.fillText(word, x, rect.y);
    ctx.fillText(word, x, rect.y + rect.h);
  }

  // left + right edges (vertical) — only when the frame is tall enough to read
  if (rect.h > 70) {
    for (const edgeX of [rect.x, rect.x + rect.w]) {
      ctx.save();
      ctx.translate(edgeX, rect.y);
      ctx.rotate(Math.PI / 2);
      for (let yy = step / 2 - shift; yy < rect.h + step; yy += step) {
        ctx.fillStyle = `hsl(${(((yy * 2.2 + time * 80) % 360) + 360) % 360}, 90%, 60%)`;
        ctx.fillText(word, yy, 0);
      }
      ctx.restore();
    }
  }
  ctx.restore();
}

function drawGatesOfHeavenFrame(
  ctx: CanvasRenderingContext2D,
  cfg: FrameRender,
  rect: FrameRect,
  time: number,
): void {
  const gold = cfg.color;
  const white = cfg.color2 ?? "#ffffff";
  const pulse = 0.5 + Math.sin(time * 2.5) * 0.3;

  // outer glow halo
  ctx.save();
  ctx.shadowColor = white;
  ctx.shadowBlur = 14 * pulse;
  ctx.strokeStyle = gold;
  ctx.lineWidth = cfg.width;
  strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
  ctx.restore();

  // inner white inset
  ctx.strokeStyle = white;
  ctx.lineWidth = 1.2;
  strokeRoundedRect(ctx, rect.x + 3, rect.y + 3, rect.w - 6, rect.h - 6, rect.radius - 1);

  // pillars at left and right
  const pillarW = 6;
  const pillarTop = rect.y;
  const pillarBot = rect.y + rect.h;
  ctx.fillStyle = gold;
  ctx.fillRect(rect.x - pillarW, pillarTop + 8, pillarW, pillarBot - pillarTop - 16);
  ctx.fillRect(rect.x + rect.w, pillarTop + 8, pillarW, pillarBot - pillarTop - 16);
  // pillar caps
  ctx.fillStyle = white;
  ctx.fillRect(rect.x - pillarW - 1, pillarTop + 6, pillarW + 2, 4);
  ctx.fillRect(rect.x - pillarW - 1, pillarBot - 10, pillarW + 2, 4);
  ctx.fillRect(rect.x + rect.w - 1, pillarTop + 6, pillarW + 2, 4);
  ctx.fillRect(rect.x + rect.w - 1, pillarBot - 10, pillarW + 2, 4);

  // top arch / keystone
  const cx = rect.x + rect.w / 2;
  ctx.fillStyle = gold;
  ctx.beginPath();
  ctx.moveTo(cx - 12, rect.y);
  ctx.quadraticCurveTo(cx, rect.y - 14, cx + 12, rect.y);
  ctx.lineTo(cx + 8, rect.y);
  ctx.quadraticCurveTo(cx, rect.y - 9, cx - 8, rect.y);
  ctx.closePath();
  ctx.fill();
  // keystone gem
  ctx.fillStyle = white;
  ctx.beginPath();
  ctx.arc(cx, rect.y - 6, 2.5, 0, Math.PI * 2);
  ctx.fill();

  // light beams streaming up from the top, animated
  ctx.save();
  ctx.globalAlpha = 0.55 * pulse;
  ctx.strokeStyle = white;
  ctx.lineWidth = 1;
  for (let i = 0; i < 5; i += 1) {
    const phase = time * 1.4 + i * 0.7;
    const x = cx + (i - 2) * 10 + Math.sin(phase) * 3;
    ctx.beginPath();
    ctx.moveTo(x, rect.y - 10);
    ctx.lineTo(x + Math.cos(phase) * 4, rect.y - 30 - Math.abs(Math.sin(phase)) * 8);
    ctx.stroke();
  }
  ctx.restore();

  // wing-like feather flecks along sides
  ctx.fillStyle = white;
  ctx.globalAlpha = 0.7;
  for (let i = 0; i < 6; i += 1) {
    const t = (time * 18 + i * 25) % 60;
    const fy = rect.y + 12 + ((i * 14 + t) % (rect.h - 24));
    ctx.fillRect(rect.x - pillarW - 4, fy, 2, 1);
    ctx.fillRect(rect.x + rect.w + pillarW + 2, fy, 2, 1);
  }
  ctx.globalAlpha = 1;
}

function strokeBasic(
  ctx: CanvasRenderingContext2D,
  color: string,
  width: number,
  rect: FrameRect,
): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
  ctx.lineWidth = 1;
}

/**
 * Row-level frame for leaderboard rows. Skips decorations that don't fit at
 * row height (~22px) — keeps only the kind's distinctive stroke style + any
 * cheap animated effect.
 */
export function drawFrameRow(
  ctx: CanvasRenderingContext2D,
  cfg: FrameRender,
  rect: FrameRect,
  time: number,
): void {
  switch (cfg.kind) {
    case "neon":
    case "obsidian": {
      const pulse = cfg.kind === "neon" ? 0.6 + Math.sin(time * 4) * 0.3 : 1;
      ctx.save();
      ctx.shadowColor = cfg.kind === "neon" ? cfg.color : (cfg.color2 ?? cfg.color);
      ctx.shadowBlur = 6 * pulse;
      ctx.strokeStyle = cfg.color;
      ctx.lineWidth = Math.max(1.5, cfg.width - 1);
      strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
      ctx.restore();
      return;
    }
    case "gates_of_heaven": {
      const pulse = 0.55 + Math.sin(time * 2.5) * 0.3;
      ctx.save();
      ctx.shadowColor = cfg.color2 ?? "#ffffff";
      ctx.shadowBlur = 8 * pulse;
      ctx.strokeStyle = cfg.color;
      ctx.lineWidth = Math.max(1.5, cfg.width - 1);
      strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
      ctx.restore();
      ctx.strokeStyle = cfg.color2 ?? "#ffffff";
      ctx.lineWidth = 0.8;
      strokeRoundedRect(ctx, rect.x + 1.5, rect.y + 1.5, rect.w - 3, rect.h - 3, rect.radius - 1);
      return;
    }
    case "creator":
    case "rainbow":
    case "holographic": {
      const grad = ctx.createLinearGradient(rect.x, rect.y, rect.x + rect.w, rect.y);
      const shift = (time * 30) % 360;
      for (let i = 0; i <= 5; i += 1) {
        const hue = (i * 72 + shift) % 360;
        grad.addColorStop(i / 5, `hsl(${hue}, 80%, 65%)`);
      }
      ctx.strokeStyle = grad;
      ctx.lineWidth = Math.max(1.5, cfg.width - 1);
      strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
      ctx.lineWidth = 1;
      return;
    }
    case "shadow":
      ctx.save();
      ctx.shadowColor = cfg.color2 ?? "#000";
      ctx.shadowBlur = 8;
      ctx.strokeStyle = cfg.color;
      ctx.lineWidth = Math.max(1.5, cfg.width - 1);
      strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
      ctx.restore();
      return;
    case "bronze":
    case "silver":
    case "gold":
    case "platinum":
    case "ruby":
    case "emerald":
    case "sapphire":
    case "diamond":
    case "frozen":
    case "flame":
    case "cosmic":
    case "lava":
    case "royal":
    case "sakura":
    case "crystal":
    case "vintage":
    case "thorn":
    case "ancient":
    case "bubble":
    case "steampunk":
    case "golden_leaf": {
      // metallic-style double stroke (no decorations)
      ctx.strokeStyle = cfg.color;
      ctx.lineWidth = Math.max(1.5, cfg.width - 1);
      strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
      if (cfg.color2) {
        ctx.strokeStyle = cfg.color2;
        ctx.lineWidth = 0.8;
        strokeRoundedRect(
          ctx,
          rect.x + 1.5,
          rect.y + 1.5,
          rect.w - 3,
          rect.h - 3,
          Math.max(2, rect.radius - 1),
        );
      }
      ctx.lineWidth = 1;
      return;
    }
    case "cyber":
      // corner brackets in a row are slick
      ctx.strokeStyle = cfg.color;
      ctx.lineWidth = 1.5;
      strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, 3);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(rect.x, rect.y + 6);
      ctx.lineTo(rect.x, rect.y);
      ctx.lineTo(rect.x + 6, rect.y);
      ctx.moveTo(rect.x + rect.w - 6, rect.y);
      ctx.lineTo(rect.x + rect.w, rect.y);
      ctx.lineTo(rect.x + rect.w, rect.y + 6);
      ctx.moveTo(rect.x, rect.y + rect.h - 6);
      ctx.lineTo(rect.x, rect.y + rect.h);
      ctx.lineTo(rect.x + 6, rect.y + rect.h);
      ctx.moveTo(rect.x + rect.w - 6, rect.y + rect.h);
      ctx.lineTo(rect.x + rect.w, rect.y + rect.h);
      ctx.lineTo(rect.x + rect.w, rect.y + rect.h - 6);
      ctx.stroke();
      ctx.lineWidth = 1;
      return;
    case "gothic":
      ctx.strokeStyle = cfg.color;
      ctx.lineWidth = cfg.width;
      strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, 3);
      if (cfg.color2) {
        ctx.strokeStyle = cfg.color2;
        ctx.lineWidth = 0.8;
        strokeRoundedRect(ctx, rect.x + 1.5, rect.y + 1.5, rect.w - 3, rect.h - 3, 2);
      }
      ctx.lineWidth = 1;
      return;
    case "pixel":
      ctx.fillStyle = cfg.color;
      for (let x = rect.x; x < rect.x + rect.w; x += 4) {
        ctx.fillRect(x, rect.y, 2, 2);
        ctx.fillRect(x, rect.y + rect.h - 2, 2, 2);
      }
      for (let y = rect.y; y < rect.y + rect.h; y += 4) {
        ctx.fillRect(rect.x, y, 2, 2);
        ctx.fillRect(rect.x + rect.w - 2, y, 2, 2);
      }
      return;
    case "custom": {
      if (cfg.animated) {
        ctx.strokeStyle = customAnimStroke(ctx, cfg, rect, time);
        ctx.lineWidth = Math.max(1.5, cfg.width - 1);
        strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
        ctx.lineWidth = 1;
      } else {
        strokeBasic(ctx, cfg.color, Math.max(1.5, cfg.width - 1), rect);
      }
      return;
    }
    case "solid":
    default:
      strokeBasic(ctx, cfg.color, Math.max(1.5, cfg.width - 1), rect);
      return;
  }
}

function drawMetallic(ctx: CanvasRenderingContext2D, cfg: FrameRender, rect: FrameRect): void {
  ctx.strokeStyle = cfg.color;
  ctx.lineWidth = cfg.width;
  strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
  if (cfg.color2) {
    ctx.strokeStyle = cfg.color2;
    ctx.lineWidth = 1;
    strokeRoundedRect(ctx, rect.x + 2, rect.y + 2, rect.w - 4, rect.h - 4, rect.radius - 2);
  }
  ctx.lineWidth = 1;
}

function drawGemFrame(ctx: CanvasRenderingContext2D, cfg: FrameRender, rect: FrameRect): void {
  drawMetallic(ctx, cfg, rect);
  if (!cfg.color2) return;
  const corners = [
    [rect.x, rect.y],
    [rect.x + rect.w, rect.y],
    [rect.x, rect.y + rect.h],
    [rect.x + rect.w, rect.y + rect.h],
  ];
  for (const [cx, cy] of corners) {
    drawDiamondShape(ctx, cx!, cy!, 4, cfg.color2, cfg.color);
  }
}

function drawDiamondFrame(ctx: CanvasRenderingContext2D, cfg: FrameRender, rect: FrameRect): void {
  drawMetallic(ctx, cfg, rect);
  // sparkle dots
  ctx.fillStyle = cfg.color2 ?? "#fff";
  for (let i = 0; i < 6; i += 1) {
    const t = i / 5;
    ctx.beginPath();
    ctx.arc(rect.x + t * rect.w, rect.y, 1.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(rect.x + t * rect.w, rect.y + rect.h, 1.2, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawNeonFrame(
  ctx: CanvasRenderingContext2D,
  cfg: FrameRender,
  rect: FrameRect,
  time: number,
): void {
  const pulse = 0.7 + Math.sin(time * 4) * 0.3;
  ctx.save();
  ctx.shadowColor = cfg.color;
  ctx.shadowBlur = 10 * pulse;
  ctx.strokeStyle = cfg.color;
  ctx.lineWidth = cfg.width;
  strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
  ctx.shadowBlur = 0;
  ctx.strokeStyle = cfg.color2 ?? "#ffffff";
  ctx.lineWidth = 1;
  strokeRoundedRect(ctx, rect.x + 2, rect.y + 2, rect.w - 4, rect.h - 4, rect.radius - 2);
  ctx.restore();
}

function drawCyberFrame(ctx: CanvasRenderingContext2D, cfg: FrameRender, rect: FrameRect): void {
  ctx.strokeStyle = cfg.color;
  ctx.lineWidth = cfg.width;
  strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, 4);
  // corner brackets
  const len = 14;
  ctx.beginPath();
  ctx.moveTo(rect.x, rect.y + len);
  ctx.lineTo(rect.x, rect.y);
  ctx.lineTo(rect.x + len, rect.y);
  ctx.moveTo(rect.x + rect.w - len, rect.y);
  ctx.lineTo(rect.x + rect.w, rect.y);
  ctx.lineTo(rect.x + rect.w, rect.y + len);
  ctx.moveTo(rect.x, rect.y + rect.h - len);
  ctx.lineTo(rect.x, rect.y + rect.h);
  ctx.lineTo(rect.x + len, rect.y + rect.h);
  ctx.moveTo(rect.x + rect.w - len, rect.y + rect.h);
  ctx.lineTo(rect.x + rect.w, rect.y + rect.h);
  ctx.lineTo(rect.x + rect.w, rect.y + rect.h - len);
  ctx.lineWidth = cfg.width + 1;
  ctx.stroke();
  ctx.lineWidth = 1;
}

function drawSteampunkFrame(
  ctx: CanvasRenderingContext2D,
  cfg: FrameRender,
  rect: FrameRect,
  time: number,
): void {
  drawMetallic(ctx, cfg, rect);
  // gears in corners
  const corners = [
    [rect.x, rect.y],
    [rect.x + rect.w, rect.y],
    [rect.x, rect.y + rect.h],
    [rect.x + rect.w, rect.y + rect.h],
  ];
  for (let i = 0; i < corners.length; i += 1) {
    const [cx, cy] = corners[i]!;
    drawGear(ctx, cx!, cy!, 5, cfg.color2 ?? cfg.color, time * (i % 2 === 0 ? 1 : -1));
  }
}

function drawGothicFrame(ctx: CanvasRenderingContext2D, cfg: FrameRender, rect: FrameRect): void {
  ctx.strokeStyle = cfg.color;
  ctx.lineWidth = cfg.width;
  strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, 4);
  // spikes at midpoints
  ctx.fillStyle = cfg.color2 ?? cfg.color;
  const positions = [
    { x: rect.x + rect.w / 2, y: rect.y, dx: 0, dy: -1 },
    { x: rect.x + rect.w / 2, y: rect.y + rect.h, dx: 0, dy: 1 },
    { x: rect.x, y: rect.y + rect.h / 2, dx: -1, dy: 0 },
    { x: rect.x + rect.w, y: rect.y + rect.h / 2, dx: 1, dy: 0 },
  ];
  for (const p of positions) {
    ctx.beginPath();
    ctx.moveTo(p.x + p.dy * 5, p.y + p.dx * 5);
    ctx.lineTo(p.x + p.dx * 9, p.y + p.dy * 9);
    ctx.lineTo(p.x - p.dy * 5, p.y - p.dx * 5);
    ctx.closePath();
    ctx.fill();
  }
}

function drawFrozenFrame(ctx: CanvasRenderingContext2D, cfg: FrameRender, rect: FrameRect): void {
  drawMetallic(ctx, cfg, rect);
  // icy crystals at corners
  ctx.strokeStyle = cfg.color2 ?? "#fff";
  ctx.lineWidth = 1;
  const corners = [
    [rect.x, rect.y],
    [rect.x + rect.w, rect.y],
    [rect.x, rect.y + rect.h],
    [rect.x + rect.w, rect.y + rect.h],
  ];
  for (const [cx, cy] of corners) {
    drawSnowflake(ctx, cx!, cy!, 4);
  }
}

function drawFlameFrame(
  ctx: CanvasRenderingContext2D,
  cfg: FrameRender,
  rect: FrameRect,
  time: number,
): void {
  drawMetallic(ctx, cfg, rect);
  // flicker tongues along top edge
  ctx.fillStyle = cfg.color2 ?? cfg.color;
  for (let i = 0; i < 8; i += 1) {
    const x = rect.x + 10 + i * ((rect.w - 20) / 7);
    const h = 5 + Math.sin(time * 6 + i) * 2;
    ctx.beginPath();
    ctx.moveTo(x - 2, rect.y);
    ctx.lineTo(x, rect.y - h);
    ctx.lineTo(x + 2, rect.y);
    ctx.closePath();
    ctx.fill();
  }
}

function drawCosmicFrame(
  ctx: CanvasRenderingContext2D,
  cfg: FrameRender,
  rect: FrameRect,
  time: number,
): void {
  drawMetallic(ctx, cfg, rect);
  ctx.fillStyle = cfg.color2 ?? "#fff";
  for (let i = 0; i < 10; i += 1) {
    const seed = i * 1.7;
    const tw = 0.5 + Math.abs(Math.sin(time * 3 + seed)) * 0.5;
    const x = rect.x + 6 + ((seed * 17) % (rect.w - 12));
    const y = rect.y + (i % 2 === 0 ? -2 : rect.h + 2);
    ctx.globalAlpha = tw;
    ctx.beginPath();
    ctx.arc(x, y, 1.2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function drawShadowFrame(ctx: CanvasRenderingContext2D, cfg: FrameRender, rect: FrameRect): void {
  ctx.save();
  ctx.shadowColor = cfg.color2 ?? "#000";
  ctx.shadowBlur = 14;
  ctx.strokeStyle = cfg.color;
  ctx.lineWidth = cfg.width;
  strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
  ctx.restore();
}

function drawLeafFrame(ctx: CanvasRenderingContext2D, cfg: FrameRender, rect: FrameRect): void {
  drawMetallic(ctx, cfg, rect);
  // leaves at corners
  const corners: [number, number, number][] = [
    [rect.x, rect.y, -0.785],
    [rect.x + rect.w, rect.y, 0.785],
    [rect.x, rect.y + rect.h, -2.356],
    [rect.x + rect.w, rect.y + rect.h, 2.356],
  ];
  for (const [cx, cy, rot] of corners) {
    drawLeaf(ctx, cx, cy, 6, rot, cfg.color, cfg.color2 ?? "#000");
  }
}

function drawCrystalFrame(ctx: CanvasRenderingContext2D, cfg: FrameRender, rect: FrameRect): void {
  drawMetallic(ctx, cfg, rect);
  // crystal spikes along top
  ctx.fillStyle = cfg.color2 ?? cfg.color;
  for (let i = 0; i < 5; i += 1) {
    const cx = rect.x + 12 + i * ((rect.w - 24) / 4);
    drawCrystalShape(ctx, cx, rect.y - 4, 5);
  }
}

function drawObsidianFrame(
  ctx: CanvasRenderingContext2D,
  cfg: FrameRender,
  rect: FrameRect,
): void {
  ctx.save();
  ctx.shadowColor = cfg.color2 ?? "#7c4dff";
  ctx.shadowBlur = 8;
  ctx.strokeStyle = cfg.color;
  ctx.lineWidth = cfg.width + 1;
  strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
  ctx.shadowBlur = 0;
  ctx.strokeStyle = cfg.color2 ?? "#7c4dff";
  ctx.lineWidth = 1;
  strokeRoundedRect(ctx, rect.x + 2, rect.y + 2, rect.w - 4, rect.h - 4, rect.radius - 2);
  ctx.restore();
}

function drawRainbowFrame(
  ctx: CanvasRenderingContext2D,
  cfg: FrameRender,
  rect: FrameRect,
  time: number,
): void {
  const grad = ctx.createLinearGradient(rect.x, rect.y, rect.x + rect.w, rect.y + rect.h);
  for (let i = 0; i <= 6; i += 1) {
    const hue = (i * 60 + time * 60) % 360;
    grad.addColorStop(i / 6, `hsl(${hue}, 85%, 60%)`);
  }
  ctx.strokeStyle = grad;
  ctx.lineWidth = cfg.width;
  strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
  ctx.lineWidth = 1;
}

function drawPixelFrame(ctx: CanvasRenderingContext2D, cfg: FrameRender, rect: FrameRect): void {
  ctx.fillStyle = cfg.color;
  const step = 4;
  for (let x = rect.x; x < rect.x + rect.w; x += step) {
    ctx.fillRect(x, rect.y, 3, 3);
    ctx.fillRect(x, rect.y + rect.h - 3, 3, 3);
  }
  for (let y = rect.y; y < rect.y + rect.h; y += step) {
    ctx.fillRect(rect.x, y, 3, 3);
    ctx.fillRect(rect.x + rect.w - 3, y, 3, 3);
  }
}

function drawVintageFrame(ctx: CanvasRenderingContext2D, cfg: FrameRender, rect: FrameRect): void {
  ctx.strokeStyle = cfg.color;
  ctx.lineWidth = cfg.width;
  strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
  // double stroke
  ctx.lineWidth = 1;
  strokeRoundedRect(ctx, rect.x + 4, rect.y + 4, rect.w - 8, rect.h - 8, rect.radius - 3);
  // ornate corner squares
  ctx.fillStyle = cfg.color2 ?? "#fff";
  const corners = [
    [rect.x, rect.y],
    [rect.x + rect.w, rect.y],
    [rect.x, rect.y + rect.h],
    [rect.x + rect.w, rect.y + rect.h],
  ];
  for (const [cx, cy] of corners) {
    ctx.fillRect(cx! - 3, cy! - 3, 6, 6);
    ctx.strokeStyle = cfg.color;
    ctx.strokeRect(cx! - 3, cy! - 3, 6, 6);
  }
}

function drawRoyalFrame(ctx: CanvasRenderingContext2D, cfg: FrameRender, rect: FrameRect): void {
  drawMetallic(ctx, cfg, rect);
  // crown emblem at top center
  const cx = rect.x + rect.w / 2;
  const cy = rect.y - 2;
  ctx.fillStyle = cfg.color2 ?? cfg.color;
  ctx.fillRect(cx - 7, cy, 14, 3);
  for (let i = 0; i < 3; i += 1) {
    ctx.beginPath();
    ctx.moveTo(cx - 6 + i * 6, cy);
    ctx.lineTo(cx - 4 + i * 6, cy - 5);
    ctx.lineTo(cx - 2 + i * 6, cy);
    ctx.closePath();
    ctx.fill();
  }
}

function drawThornFrame(ctx: CanvasRenderingContext2D, cfg: FrameRender, rect: FrameRect): void {
  ctx.strokeStyle = cfg.color;
  ctx.lineWidth = cfg.width;
  strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, 4);
  // thorns
  ctx.fillStyle = cfg.color2 ?? cfg.color;
  for (let i = 0; i < 6; i += 1) {
    const x = rect.x + 8 + i * ((rect.w - 16) / 5);
    spike(ctx, x, rect.y, 0, -1);
    spike(ctx, x, rect.y + rect.h, 0, 1);
  }
  for (let i = 0; i < 6; i += 1) {
    const y = rect.y + 8 + i * ((rect.h - 16) / 5);
    spike(ctx, rect.x, y, -1, 0);
    spike(ctx, rect.x + rect.w, y, 1, 0);
  }
}

function drawLavaFrame(
  ctx: CanvasRenderingContext2D,
  cfg: FrameRender,
  rect: FrameRect,
  time: number,
): void {
  drawMetallic(ctx, cfg, rect);
  // bubbling drips along bottom
  ctx.fillStyle = cfg.color2 ?? cfg.color;
  for (let i = 0; i < 7; i += 1) {
    const x = rect.x + 8 + i * ((rect.w - 16) / 6);
    const drip = 3 + Math.sin(time * 4 + i) * 2;
    ctx.beginPath();
    ctx.arc(x, rect.y + rect.h + drip, 2.2, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawBubbleFrame(
  ctx: CanvasRenderingContext2D,
  cfg: FrameRender,
  rect: FrameRect,
  time: number,
): void {
  ctx.strokeStyle = cfg.color;
  ctx.lineWidth = cfg.width;
  strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
  // bubbles around
  ctx.strokeStyle = cfg.color2 ?? cfg.color;
  ctx.lineWidth = 1;
  for (let i = 0; i < 12; i += 1) {
    const t = i / 12;
    const along = (t * (rect.w * 2 + rect.h * 2) + time * 8) % (rect.w * 2 + rect.h * 2);
    const p = perimeterPoint(rect, along);
    const r = 2 + (i % 3);
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    ctx.stroke();
  }
}

function drawSakuraFrame(ctx: CanvasRenderingContext2D, cfg: FrameRender, rect: FrameRect): void {
  ctx.strokeStyle = cfg.color;
  ctx.lineWidth = cfg.width;
  strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
  ctx.fillStyle = cfg.color2 ?? cfg.color;
  // petal clusters at corners
  const corners = [
    [rect.x, rect.y],
    [rect.x + rect.w, rect.y],
    [rect.x, rect.y + rect.h],
    [rect.x + rect.w, rect.y + rect.h],
  ];
  for (const [cx, cy] of corners) {
    drawFlower(ctx, cx!, cy!, 5, cfg.color, cfg.color2 ?? "#fff");
  }
}

function drawHolographicFrame(
  ctx: CanvasRenderingContext2D,
  cfg: FrameRender,
  rect: FrameRect,
  time: number,
): void {
  const grad = ctx.createLinearGradient(rect.x, rect.y, rect.x + rect.w, rect.y);
  const shift = (time * 30) % 360;
  for (let i = 0; i <= 5; i += 1) {
    const hue = (i * 72 + shift) % 360;
    grad.addColorStop(i / 5, `hsl(${hue}, 75%, 70%)`);
  }
  ctx.save();
  ctx.shadowColor = `hsl(${shift}, 75%, 70%)`;
  ctx.shadowBlur = 6;
  ctx.strokeStyle = grad;
  ctx.lineWidth = cfg.width;
  strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, rect.radius);
  ctx.restore();
  void cfg;
}

function drawAncientFrame(ctx: CanvasRenderingContext2D, cfg: FrameRender, rect: FrameRect): void {
  ctx.strokeStyle = cfg.color;
  ctx.lineWidth = cfg.width;
  strokeRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, 4);
  // rune marks along the top edge
  ctx.fillStyle = cfg.color2 ?? cfg.color;
  ctx.font = "8px ui-monospace, monospace";
  ctx.textAlign = "center";
  const runes = ["†", "‡", "§", "¶", "Ω", "Æ", "Þ"];
  for (let i = 0; i < runes.length; i += 1) {
    const x = rect.x + 12 + i * ((rect.w - 24) / (runes.length - 1));
    ctx.fillText(runes[i]!, x, rect.y - 2);
  }
  ctx.textAlign = "left";
}

// --- shape helpers ---

function drawDiamondShape(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  fill: string,
  stroke: string,
): void {
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.moveTo(cx, cy - r);
  ctx.lineTo(cx + r * 0.7, cy);
  ctx.lineTo(cx, cy + r);
  ctx.lineTo(cx - r * 0.7, cy);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 1;
  ctx.stroke();
}

function drawGear(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  color: string,
  rot: number,
): void {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rot);
  ctx.fillStyle = color;
  for (let i = 0; i < 6; i += 1) {
    const a = i * (Math.PI / 3);
    ctx.beginPath();
    ctx.arc(Math.cos(a) * r, Math.sin(a) * r, 1.2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#1a1a1a";
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.25, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawSnowflake(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
): void {
  for (let i = 0; i < 3; i += 1) {
    const a = i * (Math.PI / 3);
    ctx.beginPath();
    ctx.moveTo(cx - Math.cos(a) * r, cy - Math.sin(a) * r);
    ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
    ctx.stroke();
  }
}

function drawLeaf(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  rot: number,
  fill: string,
  vein: string,
): void {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rot);
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.ellipse(0, 0, r, r * 0.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = vein;
  ctx.lineWidth = 0.6;
  ctx.beginPath();
  ctx.moveTo(-r, 0);
  ctx.lineTo(r, 0);
  ctx.stroke();
  ctx.restore();
}

function drawCrystalShape(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(cx, cy - r);
  ctx.lineTo(cx + r * 0.5, cy);
  ctx.lineTo(cx, cy + r * 0.3);
  ctx.lineTo(cx - r * 0.5, cy);
  ctx.closePath();
  ctx.fill();
}

function spike(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  dx: number,
  dy: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + dy * 2, y + dx * 2);
  ctx.lineTo(x + dx * 5, y + dy * 5);
  ctx.lineTo(x - dy * 2, y - dx * 2);
  ctx.closePath();
  ctx.fill();
}

function perimeterPoint(rect: FrameRect, t: number): { x: number; y: number } {
  const wt = rect.w * 2 + rect.h * 2;
  const pos = t % wt;
  if (pos < rect.w) return { x: rect.x + pos, y: rect.y };
  if (pos < rect.w + rect.h) return { x: rect.x + rect.w, y: rect.y + (pos - rect.w) };
  if (pos < rect.w * 2 + rect.h)
    return { x: rect.x + rect.w - (pos - rect.w - rect.h), y: rect.y + rect.h };
  return { x: rect.x, y: rect.y + rect.h - (pos - rect.w * 2 - rect.h) };
}

function drawFlower(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  petal: string,
  center: string,
): void {
  ctx.fillStyle = petal;
  for (let i = 0; i < 5; i += 1) {
    const a = (i * Math.PI * 2) / 5;
    const px = cx + Math.cos(a) * r * 0.7;
    const py = cy + Math.sin(a) * r * 0.7;
    ctx.beginPath();
    ctx.arc(px, py, 2.2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = center;
  ctx.beginPath();
  ctx.arc(cx, cy, 1.5, 0, Math.PI * 2);
  ctx.fill();
}

